
import React, { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Edit, Trash2, AlertTriangle, CheckCircle, Package, MapPin, Scale } from 'lucide-react';
import { ArticleInventaire } from '@/hooks/useInventaire';
import { useEspacesStockage } from '@/hooks/useEspacesStockage';
import QuantityControl from './QuantityControl';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface ArticleCompactCardProps {
  article: ArticleInventaire;
  onEdit: (article: ArticleInventaire) => void;
  onDelete?: (id: string) => void;
  showActions?: boolean;
  onQuantityUpdate: (articleId: string, newQuantity: number) => Promise<void>;
}

const ArticleCompactCard = ({ 
  article, 
  onEdit, 
  onDelete, 
  showActions = true,
  onQuantityUpdate
}: ArticleCompactCardProps) => {
  const { espaces } = useEspacesStockage();
  const [localQuantity, setLocalQuantity] = useState(article.quantite);

  // Sync if article prop changes from outside
  React.useEffect(() => {
    setLocalQuantity(article.quantite);
  }, [article.quantite]);

  const getStatusInfo = (quantite: number, quantiteMax: number) => {
    const pourcentage = quantiteMax > 0 ? (quantite / quantiteMax) * 100 : 0;
    
    if (pourcentage === 0) {
      return { 
        status: 'Épuisé', 
        color: 'bg-red-100 text-red-800', 
        icon: AlertTriangle,
        iconColor: 'text-red-600'
      };
    } else if (pourcentage <= 25) {
      return { 
        status: 'Critique', 
        color: 'bg-orange-100 text-orange-800', 
        icon: AlertTriangle,
        iconColor: 'text-orange-600'
      };
    } else if (pourcentage <= 50) {
      return { 
        status: 'Faible', 
        color: 'bg-yellow-100 text-yellow-800', 
        icon: Package,
        iconColor: 'text-yellow-600'
      };
    } else {
      return { 
        status: 'Suffisant', 
        color: 'bg-green-100 text-green-800', 
        icon: CheckCircle,
        iconColor: 'text-green-600'
      };
    }
  };

  const isExpired = (datePeremption: string | null) => {
    if (!datePeremption) return false;
    return new Date(datePeremption) < new Date();
  };

  const isExpiringSoon = (datePeremption: string | null) => {
    if (!datePeremption) return false;
    const expDate = new Date(datePeremption);
    const today = new Date();
    const diffTime = expDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 30 && diffDays > 0;
  };

  const handleQuantityUpdate = useCallback(async (newQuantity: number) => {
    setLocalQuantity(newQuantity);
    await onQuantityUpdate(article.id, newQuantity);
  }, [article.id, onQuantityUpdate]);

  const statusInfo = getStatusInfo(localQuantity, article.quantite_max);
  const StatusIcon = statusInfo.icon;
  const expired = isExpired(article.date_peremption);
  const expiringSoon = isExpiringSoon(article.date_peremption);
  const espaceStockage = espaces.find(e => e.id === article.espace_stockage_id);

  return (
    <div className="border rounded-lg p-3 hover:bg-gray-50 bg-white shadow-sm">
      <div className="flex justify-between items-start mb-2">
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-sm text-nautic truncate">{article.nom}</h4>
          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1 flex-wrap">
            <Scale className="h-3 w-3" />
            <span className="font-medium text-nautic">{article.unite || 'unité'}</span>
            {espaceStockage && (
              <>
                <MapPin className="h-3 w-3" />
                <span className="font-medium text-nautic truncate">{espaceStockage.nom}</span>
              </>
            )}
          </div>
        </div>
        {showActions && (
          <div className="flex gap-1 ml-2 flex-shrink-0">
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(article);
              }}
              className="h-6 w-6 p-0"
            >
              <Edit className="h-3 w-3" />
            </Button>
            {onDelete && (
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(article.id);
                }}
                className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-1 mb-2">
        <Badge className={`${statusInfo.color} text-xs px-1 py-0`}>
          <StatusIcon className={`h-2 w-2 mr-1 ${statusInfo.iconColor}`} />
          {statusInfo.status}
        </Badge>
        
        {expired && (
          <Badge className="bg-red-100 text-red-800 text-xs px-1 py-0">
            Périmé
          </Badge>
        )}
        
        {expiringSoon && !expired && (
          <Badge className="bg-orange-100 text-orange-800 text-xs px-1 py-0">
            Expire bientôt
          </Badge>
        )}
      </div>

      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span className="text-xs font-medium text-gray-600">Stock:</span>
          <div onClick={(e) => e.stopPropagation()}>
            <QuantityControl
              quantity={article.quantite}
              maxQuantity={article.quantite_max}
              onUpdate={handleQuantityUpdate}
              size="sm"
            />
          </div>
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            / {article.quantite_max} {article.unite || 'unités'}
          </span>
        </div>

        {article.date_peremption && (
          <div className="text-xs text-muted-foreground ml-2 text-right">
            <span className={expired ? 'text-red-600 font-medium' : expiringSoon ? 'text-orange-600 font-medium' : ''}>
              {format(new Date(article.date_peremption), "dd/MM", { locale: fr })}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArticleCompactCard;
